import { useMemo } from 'react';

import {
  AppAnnouncementsApiFactory,
  Configuration,
  OnboardingApiFactory,
  RestAdministrationApiFactory,
  RestAnnouncementsApiFactory,
  RestAttachmentApiFactory,
  RestCalendarApiFactory,
  RestCarMaintenanceApiFactory,
  RestCarsApiFactory,
  RestCatalogResetApiFactory,
  RestChatApiFactory,
  RestContactsApiFactory,
  RestCustomersApiFactory,
  RestDictionaryApiFactory,
  RestDishApiFactory,
  RestDishPortionApiFactory,
  RestEmergencyContactsApiFactory,
  RestEventTypesApiFactory,
  RestExternalCustomersApiFactory,
  RestFileImportsApiFactory,
  RestLogoutApiFactory,
  RestMaintenanceApiFactory,
  RestPptxImportsApiFactory,
  RestRealizationApiFactory,
  RestReportsApiFactory,
  RestSettlementsApiFactory,
  RestStatisticsApiFactory,
  RestSuppliersApiFactory,
  RestSupportApiFactory,
  RestTasksApiFactory,
  UsersApiFactory,
} from '@/api/generated';
import { CONFIG } from '@/config/config.ts';

import { useAxiosInstance } from '../useAxiosInstance.util.tsx';

interface Return {
  administrationApi: ReturnType<typeof RestAdministrationApiFactory>;
  appAnnouncementsApi: ReturnType<typeof AppAnnouncementsApiFactory>;
  attachmentsApi: ReturnType<typeof RestAttachmentApiFactory>;
  calendarApi: ReturnType<typeof RestCalendarApiFactory>;
  carMaintenanceApi: ReturnType<typeof RestCarMaintenanceApiFactory>;
  carsApi: ReturnType<typeof RestCarsApiFactory>;
  catalogResetApi: ReturnType<typeof RestCatalogResetApiFactory>;
  chatApi: ReturnType<typeof RestChatApiFactory>;
  contactsApi: ReturnType<typeof RestContactsApiFactory>;
  customersApi: ReturnType<typeof RestCustomersApiFactory>;
  dictionariesApi: ReturnType<typeof RestDictionaryApiFactory>;
  dishesApi: ReturnType<typeof RestDishApiFactory>;
  dishPortionsApi: ReturnType<typeof RestDishPortionApiFactory>;
  emergencyContactsApi: ReturnType<typeof RestEmergencyContactsApiFactory>;
  eventTypesApi: ReturnType<typeof RestEventTypesApiFactory>;
  externalCustomersApi: ReturnType<typeof RestExternalCustomersApiFactory>;
  fileImportsApi: ReturnType<typeof RestFileImportsApiFactory>;
  logoutApi: ReturnType<typeof RestLogoutApiFactory>;
  maintenanceApi: ReturnType<typeof RestMaintenanceApiFactory>;
  onboardingApi: ReturnType<typeof OnboardingApiFactory>;
  pptxImportsApi: ReturnType<typeof RestPptxImportsApiFactory>;
  realizationApi: ReturnType<typeof RestRealizationApiFactory>;
  reportsApi: ReturnType<typeof RestReportsApiFactory>;
  settlementsApi: ReturnType<typeof RestSettlementsApiFactory>;
  statisticsApi: ReturnType<typeof RestStatisticsApiFactory>;
  suppliersApi: ReturnType<typeof RestSuppliersApiFactory>;
  supportApi: ReturnType<typeof RestSupportApiFactory>;
  tasksApi: ReturnType<typeof RestTasksApiFactory>;
  teamAnnouncementsApi: ReturnType<typeof RestAnnouncementsApiFactory>;
  usersApi: ReturnType<typeof UsersApiFactory>;
}

export const useApiClient = (): Return => {
  const basePath = CONFIG.HOST;
  const axiosInstance = useAxiosInstance();
  const configuration = useMemo(() => getConfiguration(basePath), [basePath]);

  return useMemo(() => {
    return {
      administrationApi: RestAdministrationApiFactory(configuration, undefined, axiosInstance),
      appAnnouncementsApi: AppAnnouncementsApiFactory(configuration, undefined, axiosInstance),
      attachmentsApi: RestAttachmentApiFactory(configuration, undefined, axiosInstance),
      calendarApi: RestCalendarApiFactory(configuration, undefined, axiosInstance),
      carMaintenanceApi: RestCarMaintenanceApiFactory(configuration, undefined, axiosInstance),
      carsApi: RestCarsApiFactory(configuration, undefined, axiosInstance),
      catalogResetApi: RestCatalogResetApiFactory(configuration, undefined, axiosInstance),
      chatApi: RestChatApiFactory(configuration, undefined, axiosInstance),
      contactsApi: RestContactsApiFactory(configuration, undefined, axiosInstance),
      customersApi: RestCustomersApiFactory(configuration, undefined, axiosInstance),
      dictionariesApi: RestDictionaryApiFactory(configuration, undefined, axiosInstance),
      dishesApi: RestDishApiFactory(configuration, undefined, axiosInstance),
      dishPortionsApi: RestDishPortionApiFactory(configuration, undefined, axiosInstance),
      emergencyContactsApi: RestEmergencyContactsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      eventTypesApi: RestEventTypesApiFactory(configuration, undefined, axiosInstance),
      externalCustomersApi: RestExternalCustomersApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      fileImportsApi: RestFileImportsApiFactory(configuration, undefined, axiosInstance),
      logoutApi: RestLogoutApiFactory(configuration, undefined, axiosInstance),
      maintenanceApi: RestMaintenanceApiFactory(configuration, undefined, axiosInstance),
      onboardingApi: OnboardingApiFactory(configuration, undefined, axiosInstance),
      pptxImportsApi: RestPptxImportsApiFactory(configuration, undefined, axiosInstance),
      realizationApi: RestRealizationApiFactory(configuration, undefined, axiosInstance),
      reportsApi: RestReportsApiFactory(configuration, undefined, axiosInstance),
      settlementsApi: RestSettlementsApiFactory(configuration, undefined, axiosInstance),
      statisticsApi: RestStatisticsApiFactory(configuration, undefined, axiosInstance),
      suppliersApi: RestSuppliersApiFactory(configuration, undefined, axiosInstance),
      supportApi: RestSupportApiFactory(configuration, undefined, axiosInstance),
      tasksApi: RestTasksApiFactory(configuration, undefined, axiosInstance),
      teamAnnouncementsApi: RestAnnouncementsApiFactory(configuration, undefined, axiosInstance),
      usersApi: UsersApiFactory(configuration, undefined, axiosInstance),
    };
  }, [axiosInstance, configuration]);
};

export const getConfiguration = (basePath: string): Configuration => ({
  basePath,
  isJsonMime(_mime: string): boolean {
    return true;
  },
});
